/**
 * Builds a printable document of the abbreviation list and opens the browser's
 * print dialog so the user can save it as a PDF.
 *
 * Uses a hidden iframe + window.print() rather than a PDF library: it adds no
 * dependencies (no supply-chain/security-scan surface) and the browser's
 * "Save as PDF" names the file from the document title. The document title is
 * set to "MXO Maintenance Abbreviations" so both the printed heading and the
 * suggested filename match.
 */
import { Abbreviation } from '../services/abbreviationService';

const DOCUMENT_TITLE = 'MXO Maintenance Abbreviations';
const CLEANUP_DELAY_MS = 1000;
const RENDER_DELAY_MS = 250;

/** Escapes a value for safe interpolation into the generated HTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Renders the table rows, sorted alphabetically by abbreviation. */
function buildRows(items: readonly Abbreviation[]): string {
  return [...items]
    .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
    .map((item) => {
      const standalone = item.standalone ? 'Yes' : '';
      return (
        `<tr><td>${escapeHtml(item.abbreviation)}</td>` +
        `<td>${escapeHtml(item.expansion)}</td>` +
        `<td class="c">${standalone}</td></tr>`
      );
    })
    .join('');
}

/** Builds the full printable HTML document for the abbreviation list. */
function buildDocument(items: readonly Abbreviation[]): string {
  const generatedOn = new Date().toISOString().split('T')[0];
  return (
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    `<title>${DOCUMENT_TITLE}</title><style>` +
    '@page { margin: 18mm; }' +
    'body { font-family: Arial, Helvetica, sans-serif; color: #111; }' +
    'h1 { font-size: 18pt; margin: 0 0 4pt; }' +
    '.meta { font-size: 9pt; color: #555; margin: 0 0 12pt; }' +
    'table { width: 100%; border-collapse: collapse; font-size: 10pt; }' +
    'thead { display: table-header-group; }' +
    'th, td { border: 1px solid #999; padding: 4pt 6pt; text-align: left; vertical-align: top; }' +
    'th { background: #eee; }' +
    'td.c, th.c { text-align: center; }' +
    'tr { page-break-inside: avoid; }' +
    '</style></head><body>' +
    `<h1>${DOCUMENT_TITLE}</h1>` +
    `<p class="meta">${items.length} abbreviations &middot; generated ${generatedOn}</p>` +
    '<table><thead><tr><th>Abbreviation</th><th>Expands to</th>' +
    '<th class="c">Standalone</th></tr></thead>' +
    `<tbody>${buildRows(items)}</tbody></table>` +
    '</body></html>'
  );
}

/**
 * Opens a print view of the abbreviation list so the user can save it as a PDF
 * titled "MXO Maintenance Abbreviations". No-op when the list is empty.
 */
export function downloadAbbreviationsPdf(items: readonly Abbreviation[]): void {
  if (items.length === 0) return;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDoc = frameWindow?.document;
  if (!frameWindow || !frameDoc) {
    iframe.remove();
    return;
  }

  frameDoc.open();
  frameDoc.write(buildDocument(items));
  frameDoc.close();

  const cleanup = (): void => {
    window.setTimeout(() => iframe.remove(), CLEANUP_DELAY_MS);
  };
  frameWindow.onafterprint = cleanup;

  // Let the iframe render before invoking print; clean up afterward.
  window.setTimeout(() => {
    frameWindow.focus();
    frameWindow.print();
    cleanup();
  }, RENDER_DELAY_MS);
}
